import logoIncrustation from "@images/logo_incrustation.png"
import { useUser } from "@modules/auth/hooks/useUser"
import { Periodicity } from "@modules/completedSessions/completedSessionsType"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useCompletedSessions } from "@modules/profile/publicProfile/hooks/useUserProfile"
import HuBars from "@modules/ui/components/charts/HuBars"
import HuDots from "@modules/ui/components/charts/HuDots"
import HuLines from "@modules/ui/components/charts/HuLines"
import { HUText } from "@modules/ui/components/huText"
import { HURow } from "@modules/ui/components/layout"
import { LoadingSpinner } from "@modules/ui/components/loading"
import TooltipIndicator from "@modules/ui/components/tooltipIndicator"
import { dateFormatByPeriodicity } from "@modules/utils/dateUtils"
import { Tooltip } from "primereact/tooltip"
import { useMemo, useState } from "react"
import { useTheme } from "styled-components"
import { v4 as uuidv4 } from "uuid"

export const TrainingInformation: React.FC<{}> = () => {
  const [periodicity, setPeriodicity] = useState<Periodicity>(Periodicity.WEEKLY)

  const theme = useTheme()
  const limit = useMemo(() => (periodicity === Periodicity.WEEKLY ? 5 : 0), [periodicity])
  const user = useUser()
  const { data: completedSessions, isLoading } = useCompletedSessions(user?.id ?? "", periodicity, limit)
  const language = useLanguage()

  const handlePeriodicity = (selectedPeriodicity: Periodicity) => {
    setPeriodicity(selectedPeriodicity)
  }

  const maxNbSessions = useMemo(
    () => (completedSessions ? Math.max(...completedSessions.map((session) => session.nbSessions)) : 0),
    [completedSessions],
  )

  return (
    <div
      className="relative flex flex-column flex-grow-1 bg-gradient-leGreen p-4 mb-5 rounded-33 col-6"
      style={{ height: "200px", minWidth: "575px" }}
    >
      {/* Logo */}
      <div
        className="absolute h-full w-full bg-no-repeat z-0 p-0 top-0 right-0 bg-right rounded-33 bg-contain"
        style={{ background: `url(${logoIncrustation})` }}
      ></div>
      {/* Buttons */}
      <HURow className="flex justify-content-between">
        <div className="flex flex-rows gap-2 z-1 align-items-center" style={{ minHeight: 33 }}>
          <PeriodicityButton
            isSelected={periodicity === Periodicity.WEEKLY}
            onClick={() => handlePeriodicity(Periodicity.WEEKLY)}
            text={word("trainingInformation.button.weekly")}
          />
          <PeriodicityButton
            isSelected={periodicity === Periodicity.MONTHLY}
            onClick={() => handlePeriodicity(Periodicity.MONTHLY)}
            text={word("trainingInformation.button.monthly")}
          />
          <PeriodicityButton
            isSelected={periodicity === Periodicity.YEARLY}
            onClick={() => handlePeriodicity(Periodicity.YEARLY)}
            text={word("trainingInformation.button.yearly")}
          />

          <TooltipIndicator
            className="tooltip ml-2 black"
            data-pr-showdelay={200}
            data-pr-hidedelay={200}
            data-pr-position="bottom"
          />
          <Tooltip className="tooltip-container" target=".tooltip" autoHide={false}>
            <div className="tooltip-content text-justify">
              <HUText type="label" fontStyle="BM">
                {word("trainingInformation.button.info")}
              </HUText>
            </div>
          </Tooltip>
        </div>
        {isLoading && <LoadingSpinner />}
      </HURow>
      {/* DIAGRAM */}
      <div className="flex flex-column h-full overflow-hidden z-1 mt-2">
        <div className="flex flex-rows h-full gap-3 justify-content-center">
          {!isLoading &&
            (completedSessions && completedSessions.length > 0 ? (
              completedSessions.map((session) => {
                if (periodicity === Periodicity.WEEKLY) {
                  return (
                    <HuLines
                      key={uuidv4()}
                      number={session.nbSessions}
                      date={dateFormatByPeriodicity({
                        beginDate: session.beginDate,
                        endDate: session.endDate,
                        selectedPeriodicity: periodicity,
                        language,
                      })}
                    />
                  )
                } else if (periodicity === Periodicity.MONTHLY) {
                  return (
                    <HuBars
                      key={uuidv4()}
                      number={session.nbSessions}
                      heightBar={(session.nbSessions / maxNbSessions) * 100}
                      date={dateFormatByPeriodicity({
                        beginDate: session.beginDate,
                        endDate: session.endDate,
                        selectedPeriodicity: periodicity,
                        language,
                      })}
                    />
                  )
                } else if (periodicity === Periodicity.YEARLY) {
                  return (
                    <HuDots
                      key={uuidv4()}
                      number={session.nbSessions}
                      date={dateFormatByPeriodicity({
                        beginDate: session.beginDate,
                        endDate: session.endDate,
                        selectedPeriodicity: periodicity,
                        language,
                      })}
                    />
                  )
                } else {
                  return null
                }
              })
            ) : (
              <div className="flex justify-content-center align-items-center">
                <HUText color={theme.mainBlack} fontStyle="BS" className="w-6 text-center">
                  {word("trainingInformation.emptyCompletedSessions")}
                </HUText>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

const PeriodicityButton: React.FC<{ text: string; isSelected: boolean; onClick: () => void }> = ({
  text,
  isSelected,
  onClick,
}) => {
  return (
    <div
      className={`${
        isSelected ? "bg-black" : "border-1 border-black hover:bg-primary-600"
      }  flex justify-content-center align-items-center rounded-33 px-3 h-full cursor-pointer`}
      onClick={onClick}
    >
      <HUText color={isSelected ? "white" : "black"} fontStyle="BS" className="text-justify">
        {text}
      </HUText>
    </div>
  )
}
